import { MatDialogConfig } from '@angular/material/dialog';
import { FeedbackStatus, FeedbackType, GetDocument, Region, SeriousIncidentType } from '@app/api-module/control-centre-api-client';
import { KeyValuePair } from '@app/shared/model/shared.model';

export class SearchOrderResponse {
    constructor(data?: {
        patientMrn?: string;
        patientFirstName?: string;
        patientLastName?: string;
        patientBirthDate?: string;
        accessionNumber?: string;
        trimmedAccessionNumber?: string;
        studyDate?: string;
        feedbackId?: string;
        feedbackType?: FeedbackType;
        feedbackSeriousIncidentType?: SeriousIncidentType;
        reportingRadiologist?: string;
        studyType?: string;
        feedbackStatus?: FeedbackStatus;
        urgency?: string;
        modality?: string;
        region?: Region;
        everlightResponses?: GetDocument[];
    }) {
        if (data) {
            this.patientMrn = data.patientMrn;
            this.patientFirstName = data.patientFirstName;
            this.patientLastName = data.patientLastName;
            this.patientBirthDate = data.patientBirthDate;
            this.accessionNumber = data.accessionNumber;
            this.trimmedAccessionNumber = data.trimmedAccessionNumber;
            this.studyDate = data.studyDate;
            this.feedbackId = data.feedbackId;
            this.feedbackType = data.feedbackType;
            this.feedbackSeriousIncidentType = data.feedbackSeriousIncidentType;
            this.reportingRadiologist = data.reportingRadiologist;
            this.studyType = data.studyType;
            this.feedbackStatus = data.feedbackStatus;
            this.urgency = data.urgency;
            this.modality = data.modality;
            this.region = data.region;
            this.everlightResponses = data.everlightResponses ?? [];
        }
    }

    patientMrn?: string;
    patientFirstName?: string;
    patientLastName?: string;
    patientBirthDate?: string;
    accessionNumber?: string;
    trimmedAccessionNumber?: string;
    studyDate?: string;
    feedbackId?: string;
    feedbackType?: FeedbackType;
    feedbackSeriousIncidentType?: SeriousIncidentType;
    reportingRadiologist?: string;
    studyType?: string;
    feedbackStatus?: FeedbackStatus;
    urgency?: string;
    modality?: string;
    region?: Region;
    everlightResponses: GetDocument[];
}

export class HelperMethods {
    public static getNewClinicalFeedbackMatDialogConfig() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.width = '940px';
        dialogConfig.maxHeight = '95vh';
        dialogConfig.restoreFocus = true;
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = ['new-clinical-feedback-dialog', 'draggable-dialog-container', 'resize-dialog'];
        dialogConfig.backdropClass = 'new-clinical-feedback-dialog-backdrop';
        return dialogConfig;
    }
}

export class ClinicalFeedbackDetailsInjectData {
    constructor(data: { isFromDialog: boolean; order: SearchOrderResponse }) {
        this.isFromDialog = data.isFromDialog;
        this.order = data.order;
    }
    isFromDialog: boolean;
    order: SearchOrderResponse;
}

export enum FeedbackTypeOption {
    ClinicalFeedback = 'ClinicalFeedback',
    AdvisedSeriousIncident = 'AdvisedSeriousIncident'
}

export const feedbackTypeOptionList = [
    new KeyValuePair({
        key: FeedbackTypeOption.ClinicalFeedback,
        value: 'Clinical Feedback'
    }),
    new KeyValuePair({
        key: FeedbackTypeOption.AdvisedSeriousIncident,
        value: 'Advised Serious Incident'
    })
];

const seriousIncidentTypeCrossRegion = [
    new KeyValuePair({
        key: SeriousIncidentType.DutyOfCandour,
        value: 'Duty Of Candour'
    })
];

export const seriousIncidentTypeAnz = [
    new KeyValuePair({ key: SeriousIncidentType.AimsIms, value: 'AIMS/IMS' }),
    new KeyValuePair({
        key: SeriousIncidentType.RegulatoryInvestigations,
        value: 'Regulatory Investigations'
    }),
    ...seriousIncidentTypeCrossRegion
];

export const seriousIncidentTypeUki = [
    new KeyValuePair({
        key: SeriousIncidentType.ConfirmedSeriousIncident,
        value: 'Confirmed Serious Incident / PSI'
    }),
    new KeyValuePair({
        key: SeriousIncidentType.RegulatoryInvestigations,
        value: 'Regulatory Investigations (e.g., CQC, GMC, MCIRL)'
    }),
    ...seriousIncidentTypeCrossRegion
];

export enum SeverityLevel {
    MajorDiscrepancy = 'Major Discrepancy',
    MinorDiscrepancy = 'Minor Discrepancy',
    GoodFeedback = 'Good Feedback',
    ProvideAdditionalInformation = 'Provide additional information'
}

export const incidentSeverityLevelList = [
    new KeyValuePair({
        key: SeverityLevel.MajorDiscrepancy,
        value: 'Major Discrepancy'
    }),
    new KeyValuePair({
        key: SeverityLevel.MinorDiscrepancy,
        value: 'Minor Discrepancy'
    })
];

export const feedbackSeverityLevelList = [
    ...incidentSeverityLevelList,
    new KeyValuePair({
        key: SeverityLevel.GoodFeedback,
        value: 'Good Feedback'
    }),
    new KeyValuePair({
        key: SeverityLevel.ProvideAdditionalInformation,
        value: 'Provide additional information'
    })
];

export enum ConfirmationOfIp {
    Radiologist = 'Radiologist',
    Everlight = 'Everlight',
    RadiologistAndEverlight = 'Radiologist and Everlight',
    Unknown = 'Unknown'
}

export const confirmationOfIpList = [
    new KeyValuePair({
        key: ConfirmationOfIp.Radiologist,
        value: ConfirmationOfIp.Radiologist
    }),
    new KeyValuePair({
        key: ConfirmationOfIp.Everlight,
        value: ConfirmationOfIp.Everlight
    }),
    new KeyValuePair({
        key: ConfirmationOfIp.RadiologistAndEverlight,
        value: ConfirmationOfIp.RadiologistAndEverlight
    }),
    new KeyValuePair({
        key: ConfirmationOfIp.Unknown,
        value: ConfirmationOfIp.Unknown
    })
];

export enum RadiologistsInsuranceStatus {
    OwnInsurance = 'Own Insurance',
    Corporate = 'Corporate'
}

export const radiologistInsuranceStatusList = [
    new KeyValuePair({
        key: RadiologistsInsuranceStatus.OwnInsurance,
        value: RadiologistsInsuranceStatus.OwnInsurance
    }),
    new KeyValuePair({
        key: RadiologistsInsuranceStatus.Corporate,
        value: RadiologistsInsuranceStatus.Corporate
    })
];

export enum LawyerDetails {
    CorporateInsurance = 'On corporate insurance',
    MIGA = 'MIGA'
}

export const lawyerDetailsList = [
    new KeyValuePair({
        key: LawyerDetails.CorporateInsurance,
        value: LawyerDetails.CorporateInsurance
    }),
    new KeyValuePair({ key: LawyerDetails.MIGA, value: LawyerDetails.MIGA })
];

export enum AcceptedFileMimeType {
    jpeg = 'image/jpeg',
    png = 'image/png',
    pdf = 'application/pdf',
    doc = 'application/msword',
    docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

export enum AcceptedFileExtension {
    jpeg = '.jpeg',
    jpg = '.jpg',
    png = '.png',
    pdf = '.pdf',
    doc = '.doc',
    docx = '.docx'
}

export const acceptedFiletypes = [
    new KeyValuePair({
        key: AcceptedFileMimeType.jpeg,
        value: AcceptedFileExtension.jpeg
    }),
    new KeyValuePair({
        key: AcceptedFileMimeType.jpeg,
        value: AcceptedFileExtension.jpg
    }),
    new KeyValuePair({
        key: AcceptedFileMimeType.png,
        value: AcceptedFileExtension.png
    }),
    new KeyValuePair({
        key: AcceptedFileMimeType.pdf,
        value: AcceptedFileExtension.pdf
    }),
    new KeyValuePair({
        key: AcceptedFileMimeType.doc,
        value: AcceptedFileExtension.doc
    }),
    new KeyValuePair({
        key: AcceptedFileMimeType.docx,
        value: AcceptedFileExtension.docx
    })
];

export class FormElementModel {
    constructor(data?: {
        id: string;
        fieldName?: string;
        label?: string;
        placeholder?: string;
        enableTooltip?: boolean;
        errorMessages?: { required?: string; pattern?: string; email?: string };
    }) {
        if (data) {
            this.id = data.id;
            this.fieldName = data.fieldName;
            this.label = data.label;
            this.placeholder = data.placeholder;
            this.errorMessages = data.errorMessages;
            this.enableTooltip = data.enableTooltip;
        }
    }

    id!: string;
    fieldName?: string;
    label?: string;
    placeholder?: string;
    enableTooltip?: boolean;
    errorMessages?: { required?: string; pattern?: string; email?: string };
}

export class DropdownFormElementModel extends FormElementModel {
    constructor(data?: {
        id: string;
        fieldName?: string;
        label?: string;
        placeholder?: string;
        errorMessages?: { required?: string; pattern?: string; email?: string };
        options?: Array<KeyValuePair>;
    }) {
        super(data);
        if (data) {
            this.options = data.options ?? [];
        }
    }

    options!: Array<KeyValuePair>;
}
